import React, { useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { UploadCloud, X, Loader2, CheckCircle2, Phone, Mail, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { SITE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Junk Removal",
  "Estate Cleanout",
  "Rental / Foreclosure",
  "Garage & Basement",
  "Yard / Acreage",
  "Commercial Cleanout",
  "Single-Item Pickup",
  "Other",
];

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", service: "", description: "" });
  const [photos, setPhotos] = useState([]); // {id, storage_path, original_filename, preview}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const uploadFiles = async (files) => {
    const imgs = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (imgs.length === 0) return;
    setUploading(true);
    for (const file of imgs) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, { ...res.data, preview: URL.createObjectURL(file) }]);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, {
        ...form,
        photos: photos.map(({ id, storage_path, original_filename, content_type }) => ({
          id, storage_path, original_filename, content_type,
        })),
      });
      setSubmitted(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    }
    setSubmitting(false);
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 bg-background">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Info column */}
          <div className="lg:col-span-2">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">Get in touch</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Get your free quote today
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Tell us about your job and snap a few photos of the junk. We'll get back to you with a fast, upfront estimate.
            </p>

            <div className="mt-8 space-y-5">
              {SITE.contacts.map((c) => (
                <a
                  key={c.tel}
                  data-testid={`quote-call-${c.name.toLowerCase()}`}
                  href={`tel:${c.tel}`}
                  className="flex items-start gap-4 group"
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-sm bg-accent text-white shrink-0">
                    <Phone className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-sm text-muted-foreground">Call or text {c.name}</p>
                    <p className="font-semibold text-foreground group-hover:text-accent transition-colors">{c.phone}</p>
                  </div>
                </a>
              ))}
              {[
                { icon: Mail, label: "Email", value: SITE.email },
                { icon: Clock, label: "Hours", value: `${SITE.hours} · ${SITE.hoursNote}` },
                { icon: MapPin, label: "Service Area", value: SITE.serviceAreas.join(" · ") },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4">
                  <span className="flex items-center justify-center w-10 h-10 rounded-sm bg-secondary text-accent shrink-0">
                    <c.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-sm text-muted-foreground">{c.label}</p>
                    <p className="font-medium text-foreground break-words">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-3">
            {submitted ? (
              <motion.div
                data-testid="quote-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center border border-border rounded-sm bg-secondary/30 p-12"
              >
                <CheckCircle2 className="w-16 h-16 text-accent" />
                <h3 className="mt-4 text-2xl font-bold text-foreground tracking-tight">Request received!</h3>
                <p className="mt-2 text-muted-foreground max-w-sm">
                  Thanks, {form.name.split(" ")[0]}. Our team will review your details and reach out shortly with a free quote.
                </p>
                <Button
                  data-testid="quote-new-request"
                  onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", address: "", service: "", description: "" }); setPhotos([]); }}
                  variant="outline"
                  className="mt-6 rounded-sm"
                >
                  Submit another request
                </Button>
              </motion.div>
            ) : (
              <form data-testid="quote-form" onSubmit={handleSubmit} className="border border-border rounded-sm bg-card p-6 md:p-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input data-testid="input-name" id="name" name="name" value={form.name} onChange={handleChange} placeholder="John Doe" className="mt-1.5 rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input data-testid="input-phone" id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="(780) 000-0000" className="mt-1.5 rounded-sm" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input data-testid="input-email" id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" className="mt-1.5 rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="service">Service Needed</Label>
                    <select
                      data-testid="input-service"
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="mt-1.5 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select a service…</option>
                      {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Property Address</Label>
                  <Input data-testid="input-address" id="address" name="address" value={form.address} onChange={handleChange} placeholder="Street, City, Postal Code" className="mt-1.5 rounded-sm" />
                </div>

                <div>
                  <Label htmlFor="description">Describe the job</Label>
                  <Textarea data-testid="input-description" id="description" name="description" value={form.description} onChange={handleChange} placeholder="e.g. Old couch, fridge, and a garage full of boxes…" className="mt-1.5 rounded-sm min-h-[100px]" />
                </div>

                {/* Photo upload */}
                <div>
                  <Label>Photos of the junk (optional)</Label>
                  <div
                    data-testid="photo-dropzone"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                    className={`mt-1.5 cursor-pointer border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
                      dragActive ? "border-accent bg-secondary/40" : "border-border hover:border-accent/60 bg-muted/30"
                    }`}
                  >
                    <UploadCloud className="w-8 h-8 mx-auto text-accent" />
                    <p className="mt-2 text-sm font-medium text-foreground">Drag & drop or click to upload</p>
                    <p className="text-xs text-muted-foreground">JPG, PNG, WEBP up to 10MB each</p>
                    <input
                      data-testid="photo-input"
                      ref={inputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => uploadFiles(e.target.files)}
                    />
                  </div>

                  {uploading && (
                    <p className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                    </p>
                  )}

                  {photos.length > 0 && (
                    <div data-testid="photo-previews" className="mt-3 grid grid-cols-4 gap-3">
                      {photos.map((p) => (
                        <div key={p.id} className="relative group aspect-square rounded-sm overflow-hidden border border-border">
                          <img src={p.preview} alt={p.original_filename} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            data-testid={`remove-photo-${p.id}`}
                            onClick={() => removePhoto(p.id)}
                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  data-testid="quote-submit-button"
                  type="submit"
                  disabled={submitting || uploading}
                  className="w-full h-12 rounded-sm bg-primary text-primary-foreground hover:bg-accent transition-colors text-base font-semibold"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send My Free Quote Request"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
